import React, { useCallback, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'

import { ReactComponent as CloseSVG } from 'shared/img/close.svg'
import logo from 'shared/img/logo.webp'

import { signIn } from 'features/auth/actions'
import { selectAuthCommunication } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import Button from 'components/UIKit/Button/Button'
import { Input } from 'components/UIKit/Input/Input'

import './SignIn.scss'

type InputsType = {
  username: string
  password: string
}

const b = block('sign-in')

const SignIn: React.FC = () => {
  const dispatch = useDispatch()

  const modal = useModalContext()

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeAuth = useSelector(selectLocaleDict, shallowEqual).auth
  const { isLoading } = useSelector(selectAuthCommunication, shallowEqual).login

  const [inputs, setInputs] = useState<InputsType>({
    username: '',
    password: '',
  })

  const handleChangeInput = useCallback((field: keyof InputsType, e: React.ChangeEvent<{ value: string }>) => {
    setInputs(prev => ({
      ...prev,
      [field]: e.target.value,
    }))
  }, [])

  const handleLoginClick = useCallback(() => {
    dispatch(signIn(inputs, { callback: modal.closeModal(MODAL_ROUTE_URLS.SIGN_IN()) }))
  }, [dispatch, inputs, modal])

  const handleCloseClick = useCallback(() => modal.closeModal(MODAL_ROUTE_URLS.SIGN_IN())(), [])

  const isButtonDisabled = useMemo(() => Object.values(inputs).find(item => !item) !== undefined, [inputs])

  return (
    <div className={b()}>
      <div className={b('heading')}>
        <span>{locale.login}</span>
        <CloseSVG className={b('close')} onClick={handleCloseClick} />
      </div>
      <div className={b('inputs')}>
        <div className={b('input-wrapper')}>
          <label className={b('label')}>{locale.usernameOrEmail}</label>
          <div className={b('input')}>
            <Input
              value={inputs.username}
              onChange={e => handleChangeInput('username', e)}
              placeholder={locale.usernameOrEmail}
            />
          </div>
        </div>
        <div className={b('input-wrapper')}>
          <label className={b('label')}>{localeAuth.password}</label>
          <div className={b('input')}>
            <Input
              value={inputs.password}
              onChange={e => handleChangeInput('password', e)}
              type="password"
              placeholder={localeAuth.password}
            />
          </div>
        </div>
      </div>
      <div className={b('btn-wrapper')}>
        <Button text={locale.login} callback={handleLoginClick} disabled={isButtonDisabled} loading={isLoading} />
      </div>
      <div className={b('divider')} />
      <div className={b('logo-wrapper')}>
        <img src={logo} className={b('logo')} alt="logo" />
      </div>
    </div>
  )
}

export default SignIn
